<template>
  <div v-if="documentSeries" class="flex flex-col h-full">
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedDocumentSeries?.name ? modifiedDocumentSeries.name : '-'"
      link-name="document-series"
      :mode="mode"
      :hideLayoutOptions="true"
      :is-modified="isModified"
      :is-edit-active="documentSeries.isFromManager || false"
      @edit-mode="mode = 'edit'"
      @cancel="onCancelEdit"
      @save="onSaveChanges" />

    <PerfectScrollbar class="w-full h-full py-4 space-y-2" ref="scrollbarId">
      <DocumentSeriesCreateEdit
        v-if="modifiedDocumentSeries"
        v-model="modifiedDocumentSeries"
        :mode="mode"
        :is-horizontal-layout="isHorizontalLayout"
        :name-error-message="$v.name.required.$invalid ? $v.name.required.$message : ''"
        :code-error-message="$v.code.required.$invalid ? $v.code.required.$message : ''"
        :sequence-error-message="
          $v.sequence.required.$invalid ? $v.sequence.required.$message : ''
        " />
    </PerfectScrollbar>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToastStore } from '@/stores/toast';

import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import DocumentSeriesCreateEdit from '@/components/documents/DocumentSeriesCreateEdit.vue';

import type { DocumentSeries } from '@/types/DocumentSeries';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minValue } from '@vuelidate/validators';
import { addOrEditDocumentSeries, getDocumentSeriesById } from '@/services/api/documents';
import { useLookupsStore } from '@/stores/lookups';
import { deepCopy } from '@/utils/data';

import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const { reloadLookups } = useLookupsStore();
const route = useRoute();
const router = useRouter();
const toastStore = useToastStore();

const documentSeries = ref<DocumentSeries>();
const modifiedDocumentSeries = ref<DocumentSeries>();
const isModified = ref<boolean>(false);

const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');
const confirmModalActive = ref(false);

watch(
  () => modifiedDocumentSeries.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(documentSeries.value);

    validationModel.value.name = newData?.name || '';
    validationModel.value.code = newData?.code || '';
    validationModel.value.sequence = newData?.sequence || 0;
  },
  { deep: true }
);

onMounted(async () => {
  await loadData();
});

async function loadData() {
  const responseDocumentSeries = await getDocumentSeriesById(route.params.id as string);
  if (responseDocumentSeries) {
    documentSeries.value = responseDocumentSeries;
    mode.value = 'view';
  } else {
    documentSeries.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      name: '',
      prefix: '',
      suffix: '',
      sequence: 0,
      isCancelingSeries: false,
      hasAutomaticNumbering: false,
    };
    mode.value = 'edit';
  }
  modifiedDocumentSeries.value = deepCopy<DocumentSeries>(documentSeries.value);
}

const isBackMode = ref(false);
function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!documentSeries.value?.createdDate) {
    router.push('/document-series');
  }
  mode.value = 'view';
}

function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  if (documentSeries.value)
    modifiedDocumentSeries.value = deepCopy<DocumentSeries>(documentSeries.value);
  if (!documentSeries.value?.createdDate || isBackMode.value) {
    router.push('/document-series');
  }
}

async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  mode.value = 'view';
  if (modifiedDocumentSeries.value) {
    let res = await addOrEditDocumentSeries(modifiedDocumentSeries.value);
    res ? toastStore.success({ text: 'Successfully saved' }) : toastStore.error({ text: res });
  }

  if (modifiedDocumentSeries.value) {
    documentSeries.value = deepCopy<DocumentSeries>(modifiedDocumentSeries.value);
  }

  await reloadLookups(['documentSeries']);
  await loadData();
}

const validationModel = ref({
  name: modifiedDocumentSeries.value?.name || '',
  code: modifiedDocumentSeries.value?.code || '',
  sequence: modifiedDocumentSeries.value?.sequence || 0,
});
const rules = computed(() => ({
  name: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  code: { required: helpers.withMessage($t('messages.errors.requiredField'), required) },
  sequence: { required: helpers.withMessage($t('messages.errors.requiredField'), minValue(0)) },
}));
const $v = useVuelidate(rules, validationModel, { $lazy: true });
</script>
